import { useState, useCallback, useEffect } from "react";
import { FocusEvent } from "react";

const STORAGE_KEY_PREFIX = "CERTN_formDrafts.";

export function useLocalDraft<T>(draftId?: string) {
  // This variable checks if draftId exists, if its not a string, then the draft will not work
  const storageKey =
    draftId && draftId.trim() ? `${STORAGE_KEY_PREFIX}${draftId}` : undefined;

  const [draftValues, setDraftValues] = useState<T | null>(() => {
    if (!storageKey) return null;
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? (JSON.parse(saved) as T) : null;
    } catch {
      return null;
    }
  });

  const saveDraft = useCallback(
    (values: T) => {
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify(values));
        setDraftValues(values);
      } catch (err) {
        console.error("Failed to save draft", err);
      }
    },
    [storageKey]
  );

  const clearDraft = useCallback(() => {
    if (!storageKey) return;
    try {
      localStorage.removeItem(storageKey);
      setDraftValues(null);
    } catch (err) {
      console.error("Failed to clear draft", err);
    }
  }, [storageKey]);

  const handleFormikAutoSaveBlur = useCallback(
    (
      handleBlur: (e: FocusEvent<HTMLDivElement>) => void,
      e: FocusEvent<HTMLDivElement>,
      values: T
    ) => {
      handleBlur(e);
      saveDraft(values);
    },
    [saveDraft]
  );

  // Sync drafts across tabs/windows
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === storageKey) {
        try {
          setDraftValues(
            event.newValue ? (JSON.parse(event.newValue) as T) : null
          );
        } catch (err) {
          console.error("Failed to parse draft from storage event", err);
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [storageKey]);

  return { draftValues, saveDraft, clearDraft, handleFormikAutoSaveBlur };
}
